import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import SEO from "@/lib/seo";
import { PageHero } from "@/components/Primitives";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await register(form.name, form.email, form.password);
      toast.success("Account created. Welcome to the Institute.");
      const next = params.get("next") || "/dashboard";
      navigate(next);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="register-page">
      <SEO title="Create account · The Cleanability Institute" description="Join the Institute and access member-only resources." path="/register" />
      <PageHero testId="register-hero" eyebrow="Create account" title="Join the discipline." lead="Free account opens the public research library, newsletter archive, and the path to membership." />
      <section className="container-x pb-24">
        <div className="max-w-md">
          <form onSubmit={submit} data-testid="register-form" className="rounded-3xl border border-border bg-card p-8 space-y-5">
            <Field label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testId="register-name" />
            <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testId="register-email" />
            <Field label="Password (min 8)" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} required testId="register-password" />
            <button
              type="submit"
              disabled={submitting}
              data-testid="register-submit"
              className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-60"
            >
              {submitting ? "Creating…" : "Create account"} <ArrowRight className="h-4 w-4" />
            </button>
            <div className="text-xs text-muted-foreground text-center">
              Already have an account? <Link to="/login" data-testid="register-to-login" className="underline-link font-medium text-foreground">Sign in</Link>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, required, testId }) {
  return (
    <label className="block">
      <div className="text-xs font-mono-mini text-muted-foreground">{label}</div>
      <input
        data-testid={testId}
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full h-11 px-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent))]"
      />
    </label>
  );
}
